import { useLocation } from '@gatsbyjs/reach-router';
import type { RouteComponentProps } from '@reach/router';
import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { array, object, string } from 'yup';

import { FormikCodeField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import AuthLayout, { BackLink, Subtitle } from '@layouts/AuthLayout';

const messages = defineMessages({
  title: {
    id: 'mfaLogin.title',
    defaultMessage: "We want to make sure it's you.",
  },
  totpSubtitle: {
    id: 'mfaLogin.totpSubtitle',
    defaultMessage: 'Enter the code shown in your authenticator app to log in',
  },
  smsSubtitle: {
    id: 'mfaLogin.smsSubtitle',
    defaultMessage:
      'Enter the code sent to your phone number {number} to log in',
  },
  emailOtpSubtitle: {
    id: 'mfaLogin.emailOtpSubtitle',
    defaultMessage: 'Enter the code sent to your email {destination} to log in',
  },
  goBack: {
    id: 'GLOBAL.goBack',
    defaultMessage: 'Back to login',
  },
  verify: {
    id: 'GLOBAL.verify',
    defaultMessage: 'Verify',
  },
  codeRequired: {
    id: 'mfaLogin.codeRequired',
    defaultMessage: 'Authentication code is required',
  },
  error: {
    id: 'mfaLogin.error',
    defaultMessage: '{text} Please try logging in again.',
  },
  expiredCode: {
    id: 'GLOBAL.expiredCode',
    defaultMessage: 'Code expired.',
  },
  incorrectCode: {
    id: 'GLOBAL.incorrectCode',
    defaultMessage: 'Incorrect code.',
  },
  globalError: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

type FormValues = {
  code: string[];
};

const MfaForm: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { respondToAuthChallenge } = useSession();

  const location = useLocation();
  const state = location.state as any;
  const challengeName =
    state && 'challengeName' in state ? state.challengeName : null;
  const email = state && 'email' in state ? state.email : null;
  const phoneNumber =
    state && 'phoneNumber' in state ? state.phoneNumber : null;

  const emailDestination =
    state && 'emailDestination' in state ? state.emailDestination : null;

  const isTotpChallenge = challengeName === 'SOFTWARE_TOKEN_MFA';
  const isSmsChallenge = challengeName === 'SMS_MFA';
  const isEmailOtpChallenge = challengeName === 'EMAIL_OTP';

  useEffect(() => {
    if (
      !email ||
      !challengeName ||
      (challengeName === 'SMS_MFA' && !phoneNumber)
    ) {
      navigate(ROUTES.LOGIN);
    }
  }, [challengeName, email, phoneNumber]);

  const validationSchema = object().shape({
    code: array()
      .of(string().max(1).required(formatMessage(messages.codeRequired)))
      .required(formatMessage(messages.codeRequired)),
  });

  async function handleSubmit({ code }: FormValues) {
    if (!challengeName) {
      return;
    }

    const challengeResponses: Record<string, string> = {
      USERNAME: email,
    };
    if (isTotpChallenge) {
      challengeResponses['SOFTWARE_TOKEN_MFA_CODE'] = code.join('');
    }
    if (isSmsChallenge) {
      challengeResponses['SMS_MFA_CODE'] = code.join('');
    }
    if (isEmailOtpChallenge) {
      challengeResponses['EMAIL_OTP_CODE'] = code.join('');
    }
    try {
      await respondToAuthChallenge(challengeName, challengeResponses);
    } catch (errors: any) {
      const errorMessage = errors[0]?.code;
      const text = errorMessage?.startsWith('CodeMismatchException')
        ? formatMessage(messages.incorrectCode)
        : errorMessage?.startsWith('ExpiredCodeException')
          ? formatMessage(messages.expiredCode)
          : formatMessage(messages.globalError);

      enqueueSnackbar({
        message: formatMessage(messages.error, { text }),
        variant: 'error',
      });
    }
  }

  return (
    <AuthLayout title={formatMessage(messages.title)}>
      <Subtitle>
        {challengeName === 'SOFTWARE_TOKEN_MFA'
          ? formatMessage(messages.totpSubtitle)
          : challengeName === 'SMS_MFA'
            ? formatMessage(messages.smsSubtitle, { number: phoneNumber })
            : challengeName === 'EMAIL_OTP'
              ? formatMessage(messages.emailOtpSubtitle, {
                  destination: emailDestination,
                })
              : ''}
      </Subtitle>

      <Formik
        enableReinitialize
        initialValues={{
          code: ['', '', '', '', '', ''],
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <Field name="code">
              {({ field }: FieldProps<string>) => (
                <FormikCodeField {...field} codeLength={values.code.length} />
              )}
            </Field>

            <RoundedButton disabled={isSubmitting} height={40} type="submit">
              {formatMessage(messages.verify)}
            </RoundedButton>
          </Form>
        )}
      </Formik>
      <BackLink to={ROUTES.LOGIN}>{formatMessage(messages.goBack)}</BackLink>
    </AuthLayout>
  );
};

export default MfaForm;
